import { ChangeEvent, DragEvent, FC, KeyboardEvent, ReactNode, useRef, useState } from 'react';

import styles from './PunchupDropZone.module.css';

export interface PunchupDropZoneProps {
	/** Value for the hidden `<input accept>` — e.g. "image/png,image/jpeg" or ".csv,.xlsx,.xls". */
	accept?: string;
	/** Called with the single selected File on click-to-pick or drop. */
	onFileSelect: (file: File) => void;
	/** When true, click / drag / drop are inert and the zone is dimmed. */
	disabled?: boolean;
	/** Extra class on the droppable container — use to add min-height / sizing. */
	className?: string;
	/** Inner content centred inside the zone (an upload invitation, an image preview, …). */
	children: ReactNode;
}

/**
 * PunchupDropZone — shared dashed-border drop shell. Owns drag/click/drop + the
 * hidden file input and emits a single File via `onFileSelect`; renders no
 * content of its own (callers pass an invitation or preview as `children`).
 * Composition contract and current callers: see ./CLAUDE.md.
 */
export const PunchupDropZone: FC<PunchupDropZoneProps> = ({
	accept,
	onFileSelect,
	disabled = false,
	className,
	children,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragActive, setIsDragActive] = useState(false);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0];
		if (picked) onFileSelect(picked);
		// Clear the value so picking the same file again still fires `change` (replace flow).
		e.target.value = '';
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		if (disabled) return;
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
		if (disabled) return;
		const dropped = e.dataTransfer.files?.[0];
		if (dropped) onFileSelect(dropped);
	};

	const handleClick = () => {
		if (!disabled) inputRef.current?.click();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (disabled) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleClick();
		}
	};

	const classes = [
		styles.dropZone,
		isDragActive && !disabled ? styles.dropZoneDragActive : '',
		disabled ? styles.dropZoneDisabled : '',
		className ?? '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div
			className={classes}
			role="button"
			tabIndex={disabled ? -1 : 0}
			aria-label="Upload a file"
			aria-disabled={disabled}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{children}
			<input
				ref={inputRef}
				className={styles.hiddenInput}
				type="file"
				accept={accept}
				disabled={disabled}
				// Stop the programmatic click (from handleClick) bubbling back to the
				// zone, which would re-open the picker.
				onClick={(e) => e.stopPropagation()}
				onChange={handleInputChange}
			/>
		</div>
	);
};
